/*
 * Project: Goalscape
 * File:    saveUtils.js
 * Path:    src/utils/saveUtils.js
 * Description: Utility functions for managing user saves (bookmarks) in Firebase.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement server-side functions or triggers to automatically fetch and update save metadata.
 */
import { db } from '../firebase';
import {
    collection,
    addDoc,
    getDocs,
    deleteDoc,
    doc,
    updateDoc,
    serverTimestamp,
    deleteField
} from 'firebase/firestore';
import { fetchUrlMetadata } from './bookmarkUtils';

/*
 * Function: initializeUserSaves
 * Description: Fetches and initializes a user's saves collection from Firebase.
 * Parameters:
 *   userId: string - The ID of the user.
 *   showArchived: boolean - Whether to include archived saves (default: false).
 * Returns: Promise<Array> - Array of save objects.
 * Usage:
 *   const saves = await initializeUserSaves('user123', false);
 * AI Hint: Implement pagination for large saves collections to improve performance.
 */
export const initializeUserSaves = async (userId, showArchived = false) => {
    try {
        const savesCollection = collection(db, `users/${userId}/saves`);
        const savesSnapshot = await getDocs(savesCollection);
        const savesList = savesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            archived: doc.data().archived || false,
            read: doc.data().read || false
        }));
        
        // Filter in memory
        return savesList.filter(save => save.archived === showArchived);
    } catch (error) {
        console.error("Error initializing user saves:", error);
        throw error;
    }
};

export const moveSaveToTab = async (userId, saveId, tabId) => {
    try {
        const saveDoc = doc(db, `users/${userId}/saves/${saveId}`);
        const updates = {
            updatedAt: serverTimestamp()
        };

        if (tabId) {
            updates.tabId = tabId;
        } else {
            updates.tabId = deleteField();
        }

        await updateDoc(saveDoc, updates);
    } catch (error) {
        console.error("Error moving save to tab:", error);
        throw error;
    }
};

/*
 * Function: addSave
 * Description: Adds a new save to a user's collection.
 * Parameters:
 *   userId: string - The ID of the user.
 *   save: object - The save data to add.
 * Returns: Promise<object> - The newly created save with its ID.
 * Usage:
 *   const newSave = await addSave('user123', { url: 'https://example.com' });
 * AI Hint: Implement server-side validation for save data to prevent invalid entries.
 */
export const addSave = async (userId, save) => {
    if (!userId) throw new Error("User ID is required to add a save.");
    try {
        const savesCollection = collection(db, `users/${userId}/saves`);

        // Ensure tags are an array if not provided
        const tags = save.tags && Array.isArray(save.tags) ? save.tags : [];

        const docRef = await addDoc(savesCollection, {
            ...save,
            userId,
            createdAt: serverTimestamp(),
            archived: false,
            read: false,
            tags: tags,
        });
        return { id: docRef.id, ...save, archived: false, read: false, createdAt: new Date(), tags: tags };
    } catch (error) {
        console.error("Error adding save:", error);
        throw error;
    }
};

/*
 * Function: removeSave
 * Description: Removes a save from a user's collection.
 * Parameters:
 *   userId: string - The ID of the user.
 *   saveId: string - The ID of the save to remove.
 * Returns: Promise<void>
 * Usage:
 *   await removeSave('user123', 'save456');
 * AI Hint: Implement soft delete instead of permanent deletion for data recovery.
 */
export const removeSave = async (userId, saveId) => {
    try {
        const saveDoc = doc(db, `users/${userId}/saves/${saveId}`);
        await deleteDoc(saveDoc);
    } catch (error) {
        console.error("Error removing save:", error);
        throw error;
    }
};

/*
 * Function: toggleArchiveSave
 * Description: Toggles the archived status of a save.
 * Parameters:
 *   userId: string - The ID of the user.
 *   saveId: string - The ID of the save.
 *   archived: boolean - The new archived status.
 * Returns: Promise<void>
 * Usage:
 *   await toggleArchiveSave('user123', 'save456', true);
 * AI Hint: Implement audit logging to track save archive status changes for data analysis.
 */
export const toggleArchiveSave = async (userId, saveId, archived) => {
    try {
        const saveDoc = doc(db, `users/${userId}/saves/${saveId}`);
        await updateDoc(saveDoc, {
            archived,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error("Error toggling save archive status:", error);
        throw error;
    }
};

/*
 * Function: markSaveAsRead
 * Description: Marks a save as read and updates the read timestamp.
 * Parameters:
 *   userId: string - The ID of the user.
 *   saveId: string - The ID of the save.
 * Returns: Promise<void>
 * Usage:
 *   await markSaveAsRead('user123', 'save456');
 * AI Hint: Implement tracking of read progress within a save (e.g., for articles or documents).
 */
export const markSaveAsRead = async (userId, saveId, readStatus = true) => {
    try {
        const saveDoc = doc(db, `users/${userId}/saves/${saveId}`);
        await updateDoc(saveDoc, {
            read: readStatus,
            readAt: readStatus ? serverTimestamp() : null,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error("Error marking save as read:", error);
        throw error;
    }
};

/*
 * Function: updateSave
 * Description: Updates a save's properties.
 * Parameters:
 *   userId: string - The ID of the user.
 *   saveId: string - The ID of the save.
 *   updates: object - The properties to update.
 * Returns: Promise<void>
 * Usage:
 *   await updateSave('user123', 'save456', { title: 'New Title' });
 * AI Hint: Implement optimistic updates to improve UI responsiveness when updating save properties.
 */
export const updateSave = async (userId, saveId, updates) => {
    try {
        const saveDoc = doc(db, `users/${userId}/saves/${saveId}`);
        await updateDoc(saveDoc, {
            ...updates,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error("Error updating save:", error);
        throw error;
    }
};
